import { Suspense } from 'react';

import { PerformanceStatusPill } from '@/components/base/performance-status-pill';
import { SourcingStatusPill } from '@/components/base/sourcing-status-pill';
import { OpeningStatus } from '@/components/ui/opening-status';
import { Title } from '@/components/ui/title';
import { useOpeningDetails } from '@/contexts/opening-context';
import { formatShortDate } from '@/utils/date-util';

import { AutomationStatusPill } from './automation-status-pill';
import { HiringGoal } from './hiring-goal';
import { Tabs } from './tabs';
import { useEditTargets } from './use-edit-targets';

function OverviewContent() {
  const { opening, hiringGoal, analyticsData } = useOpeningDetails();
  const performanceStatusMetrics = hiringGoal?.performance_status_metrics;
  const { status, criteria } = performanceStatusMetrics ?? {};

  useEditTargets();

  return (
    <div className="shadow-base flex flex-col gap-4 rounded border-b border-neutral-200 bg-white px-6 pt-5">
      <div className="flex flex-col items-start gap-6 py-1.5 lg:flex-row lg:justify-between lg:gap-4">
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-1">
            <OpeningStatus active={opening.isActive} />
            <Title title={opening.title} />
          </div>

          <div className="flex items-center gap-2">
            <SourcingStatusPill status={opening.sourcingStatus} />
            {status && (
              <PerformanceStatusPill
                status={status}
                criteria={criteria ?? null}
                expected={
                  (criteria === 'hire'
                    ? performanceStatusMetrics?.expected_hires_count
                    : performanceStatusMetrics?.expected_applicants_count) ??
                  null
                }
                actual={
                  (criteria === 'hire'
                    ? analyticsData.hire_stats.actual
                    : analyticsData.applicants_stats.actual) ?? null
                }
                startDate={
                  hiringGoal?.start_date &&
                  formatShortDate(hiringGoal?.start_date)
                }
                endDate={
                  hiringGoal?.hire_by && formatShortDate(hiringGoal?.hire_by)
                }
                duration={hiringGoal?.duration}
                manageHiringGoalLink={hiringGoal?.url}
              />
            )}
            {opening.hasActiveHiringGoal && (
              <AutomationStatusPill automation={opening.automation} />
            )}
          </div>
        </div>

        <HiringGoal />
      </div>
      <Tabs />
    </div>
  );
}

export function Overview() {
  return (
    <Suspense fallback={<Overview.Skeleton />}>
      <OverviewContent />
    </Suspense>
  );
}

Overview.Skeleton = function OverviewSkeleton() {
  return (
    <div className="flex flex-col gap-4 border-b border-neutral-200 bg-white px-6 pt-5">
      <div className="flex items-center justify-between py-1.5">
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-2">
            <OpeningStatus.Skeleton />
            <Title.Skeleton />
          </div>
          <div className="flex items-center gap-2">
            <SourcingStatusPill.Skeleton />
            <PerformanceStatusPill.Skeleton />
            <AutomationStatusPill.Skeleton />
          </div>
        </div>
        <HiringGoal.Skeleton />
      </div>
      <Tabs />
    </div>
  );
};
